import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { css } from '@emotion/react'
//data
import useRawState from "../../hooks/useRawState"
import useRawPromise from "../../hooks/useRawPromise"
import Pitch from "../../context/Pitch"
import PitchListContext from "../../context/PitchList"
import EventFilterContext from "../../context/EventFilter"
//utilities
import { getMonth, getYear, format, getUnixTime, addDays, startOfDay, endOfDay } from "date-fns"
import { groupBy, countBy } from "lodash"
//components
import PageTitle from "../_general/PageTitle"
import LoaderText from "../_general/LoaderText"
import MonthlyCalendar from "../_general/MonthlyCalendar"
import ContainerDisabler from "../_general/ContainerDisabler"
import MonthBrowser from "../_control/MonthBrowser"
import EventTypeIcon from "../events/EventTypeIcon"
import EventTypeList from "../events/EventTypeList"
import DaySummaryModal from "../events/DaySummaryModal"
import DayDropZone from "../events/DayDropZone"
import FancyDropdown from "../_control/FancyDropdown"
import Modal from "../_layout/Modal"
import EventWrapper from "../modals/EventWrapper"
import { NavLink, useSearchParams } from "react-router"
import { ReactSVG } from "react-svg"
//assets
import { colors } from "shared-ui"
import filterImg from '../../assets/icons/filter.svg'
import { MTM_LIST_TYPES, MTM_EVENT_TYPES, MTM_VARIABLES } from "../../utils/matomo"
import useTracker from "../../hooks/useTracker"
import CalendarContext from "../../context/Calendar"
import { FormattedMessage, useIntl } from "react-intl"
import useBridge from "../../hooks/useBridge"
import { toZonedTime, fromZonedTime } from "date-fns-tz"

function PitchEventCalendarDev({ ...props }) {

    const { data: eventTypes, loading: loadingEventTypes } = useBridge('/api/current/frontend-bridge/event-types')

    const { formatMessage } = useIntl()

    const [params, setSearchParams] = useSearchParams()

    const track = useTracker()

    const { pitches } = useContext(PitchListContext)
    const { setSelectedDay } = useContext(CalendarContext)
    const { data: pitch = {} } = useContext(Pitch)
    const [month, setMonth] = useState(getMonth(new Date()))
    const [year, setYear] = useState(getYear(new Date()))

    const pitchFilterOptions = useMemo(() => {
        return [
            {
                value: "none",
                label: formatMessage({ id: 'none' })
            },
            ...pitches?.map(({ id, name }) => ({
                value: id,
                label: name,
            }))
        ]
    }, [pitches])

    const onChangePitches = useCallback((pitchId) => {
        const label = pitchFilterOptions.find(pitchOption => pitchOption.value === pitchId)?.label
        track({
            'event': MTM_EVENT_TYPES['option-select'],
            [MTM_VARIABLES['list-type']]: MTM_LIST_TYPES['pitch-select'],
            [MTM_VARIABLES['list-value']]: label
        })
        setSearchParams({ pitch: pitchId })
    }, [pitchFilterOptions])

    const [fromUnix, toUnix, timeZone] = useMemo(() => {
        const timeZone = pitch?.venue?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
        // time for pitch
        return [
            getUnixTime(fromZonedTime(new Date(year, month, 1), timeZone)),
            getUnixTime(fromZonedTime(new Date(year, month + 1, 1), timeZone)),
            timeZone,
        ]
    }, [pitch, month, year])

    const dateToKey = useCallback((date) => {
        return format(toZonedTime(date, timeZone), "y-M-d")
    }, [timeZone])

    const { data: events, isBusy = false } = useRawState(
        `/api/current/pitches/${pitch?.id}/events`,
        {
            from: fromUnix,
            to: toUnix,
        },
        [pitch?.id, fromUnix, toUnix]
    )

    const [fakeEvents, setFakeEvents] = useState([])
    const [allEvents, setAllEvents] = useState([])

    // if events change (eg. we got new data from the api), drop addedEvents
    useEffect(() => {
        setFakeEvents([])
        if (events) setAllEvents(events)
    }, [events])

    const { eventsPerDay, combinedEvents } = useMemo(() => {
        const combinedEvents = allEvents ? [...allEvents.sort((a, b) => a.id - b.id), ...fakeEvents] : {}
        return {
            eventsPerDay: groupBy(
                combinedEvents,
                ({ timestamp }) => dateToKey(new Date(timestamp * 1000))
            ),
            combinedEvents,
        }
    }, [allEvents, fakeEvents])

    const [selectedEventType, setSelectedEventType] = useState()
    const [selectedEvent, setSelectedEvent] = useState()
    const [daySummaryData, setDaySummaryData] = useState([])
    const [filter, setFilter] = useContext(EventFilterContext)

    const handleModalSubmit = useCallback((success, data) => {
        // close modal
        setSelectedEventType(null)
        setSelectedEvent(null)
        // clean up fake event
        setFakeEvents(current => current?.slice(0, -1) ?? [])
        if (success) {
            setAllEvents(allEvents => {
                if (allEvents.find(e => e.id === data.id)) { // PUT
                    // track calendar event update
                    track({
                        'event': MTM_EVENT_TYPES["update-event"],
                        [MTM_VARIABLES["event-type"]]: data.type
                    })
                    // Replacing the updated object
                    const removedOldEvent = allEvents.filter(e => e.id !== data.id)
                    return [...removedOldEvent, data]
                } else { // POST
                    // track calendar event creation
                    track({
                        'event': MTM_EVENT_TYPES["create-event"],
                        [MTM_VARIABLES["event-type"]]: data.type
                    })
                    // adding
                    return [...allEvents, data]
                }

            })
            if (daySummaryData) {
                // Updating the event of the selected day
                setDaySummaryData(daySummaryData => {
                    const nodeEvent = daySummaryData.find(n => n.id === data.id)
                    const pos = daySummaryData.indexOf(nodeEvent)
                    const replacedNode = [...daySummaryData]
                    replacedNode[pos] = data
                    return replacedNode
                })
            }
        }
    })

    const getFormHeader = useCallback(() => {
        if (selectedEvent) return `${formatMessage({ id: 'edit' })} / ${eventTypes?.find(x => x.entityShortName === selectedEvent.type)?.entityShortNameLocalised}`
        return `${formatMessage({ id: 'add' })} / ${selectedEventType.entityShortNameLocalised}`
    }, [selectedEvent, selectedEventType, eventTypes])

    const { del } = useRawPromise()
    const deleteEvent = useCallback(async event => {
        const { data, error } = await del(`${event["@id"]}`)
        if (!error) {
            track({
                'event': MTM_EVENT_TYPES["delete-event"],
                [MTM_VARIABLES["event-type"]]: event.type
            })
            setDaySummaryData(daySummaryData.filter(n => n.id !== event.id))
            setAllEvents(allEvents => allEvents.filter(e => e.id !== event.id))
        }
        return { error, data }
    }, [allEvents, daySummaryData])

    const handleDrop = useCallback(async (date, { eventType }) => {
        if (pitch && eventType) {
            // immediatly add fake event, for dat sweet UX
            const fakeEvent = {
                name: "name TBD",
                timestamp: getUnixTime(date),///
                pitch: pitch["@id"],
                type: eventType.entityShortName,
                fakeId: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER) // extremely tiny chance of ID collisions, we'll risk it
            }
            setFakeEvents(current => [...current, fakeEvent])
            //data for input modal
            setSelectedDay(date)
            setSelectedEventType(eventType)
        }
    }, [pitch])

    const handleClearFilter = useCallback((e) => {
        e.stopPropagation()
        setFilter([])
        track({
            'event': MTM_EVENT_TYPES["unfilter-calendar"],
        })
    }, [])

    const multiDayEventLines = useMemo(() => {
        const multiDayEvents = combinedEvents?.filter(e => e.multiDay) ?? []
        // per date key, we need an array of multidayevents that run through that day, as well as the interval (start% end% of that day).
        const dayLines = []

        for (const multiDayEvent of multiDayEvents) {
            const { timestamp, timestampEnd, type } = multiDayEvent
            if (!filter.includes(type) && filter.length > 0) continue

            const start = new Date(timestamp * 1000)
            const end = new Date(timestampEnd * 1000)
            const startOnPitch = toZonedTime(start, timeZone)
            const endOnPitch = toZonedTime(end, timeZone)

            let current = startOfDay(startOnPitch)
            const lastDay = startOfDay(endOnPitch)

            // for each event, iterate over all days it spans, and figure out how what interval within the day is occupied by the event.
            while (current <= lastDay) {
                const key = format(current, "y-M-d")
                // start and end of currently iterated day
                const dayStart = startOfDay(current).getTime()
                const dayEnd = endOfDay(current).getTime()

                const affectedStart = Math.max(startOnPitch.getTime(), dayStart)
                const affectedEnd = Math.min(endOnPitch.getTime(), dayEnd)

                const dayDuration = dayEnd - dayStart
                const startPercentage = ((affectedStart - dayStart) / dayDuration) * 100
                const endPercentage = ((affectedEnd - dayStart) / dayDuration) * 100

                dayLines.push({
                    startOnPitch,
                    endOnPitch,
                    startPercentage,
                    endPercentage,
                    key,
                    eventId: multiDayEvent.id,
                    event: multiDayEvent,
                })

                current = addDays(current, 1) // Move to next day accurately
            }
        }
        const dayGrouped = groupBy(dayLines, line => line.key)
        return dayGrouped
        // when hovering over a multi-day-event, it should alter a state that should be in the dependencies of this hook.
        // in that case, we will only render that line for the entire page.
    }, [combinedEvents, filter])
    // later, for a given day, if only one multi is there, just draw it.        
    // if multiple, check for overlap. If none overlap, render each one (absolutely positioned)
    // if any overlap, render a grayed out line fully covering the day.

    return (
        <>
            <div css={css`display: flex; flex-direction: column; align-items: stretch; height: 100%;`}
                {...props}
            >
                <PageTitle css={css`margin-bottom: 0.25em;`}>
                    <span>
                        {pitch.id ? (
                            isBusy ?
                                <LoaderText subtle />
                                :
                                <>
                                    <NavLink
                                        to={!!pitch.club?.id ? `/club/${pitch.club?.id}` : undefined}
                                        css={css`
                                            color: inherit;
                                            cursor: ${!!pitch.club?.id ? 'pointer' : 'unset'};
                                            pointer-events: ${!!pitch.club?.id ? 'unset' : 'none'};
                                        `}
                                    >
                                        {pitch.club?.name || formatMessage({ id: 'noClub' })}
                                    </NavLink>
                                    {' / '}
                                    <NavLink to={`/pitch/${pitch.id}`} css={css`color: inherit;`}>
                                        {pitch.name}
                                    </NavLink>
                                </>
                        ) :
                            <LoaderText subtle>
                                <FormattedMessage id='noPitchSelected' />
                            </LoaderText>
                        }
                    </span>
                    <span css={css`color: ${colors.main1};`}>
                        {" "} / <FormattedMessage id='events' />
                    </span>
                </PageTitle>
                <div css={css`display: flex; align-items: center;`}>
                    <div css={css`
                        display: flex; 
                        align-items: center; 
                        justify-content: center;
                        flex-grow: 1; 
                        position: relative;
                    `}>
                        <ContainerDisabler disable={!pitch?.id} />
                        <MonthBrowser
                            month={month}
                            year={year}
                            setMonth={setMonth}
                            setYear={setYear}
                        />
                    </div>
                    <FancyDropdown
                        options={pitchFilterOptions}
                        placeholder={formatMessage({ id: 'selectAPitch' })}
                        css={styles.pitchSelector}
                        value={
                            params.get('pitch') ?
                                pitchFilterOptions?.find(({ value }) => value.toString() === (params.get('pitch')))
                                :
                                undefined}
                        onChange={({ value }) => onChangePitches(value)}
                    />
                </div>
                <div css={[
                    css`display: flex; flex-basis: 0; flex-grow: 1;`,
                    styles.calendarAndEventsWrapper
                ]}>
                    <ContainerDisabler disable={!pitch?.id} />
                    <MonthlyCalendar
                        css={styles.calendar}
                        month={month}
                        year={year}
                        dateKeyMapper={dateToKey}
                        busy={isBusy}
                        onDayClick={date => {
                            const dateKey = dateToKey(date)
                            if (dateKey in eventsPerDay) {
                                setDaySummaryData(eventsPerDay[dateKey])
                            }
                        }}
                        renderDay={(date, node) => (
                            <DayDropZone
                                onDrop={event => handleDrop(date, event)}
                                date={date}
                                key={`dropzone-${getUnixTime(date)}`}
                            >
                                {node}
                                {multiDayEventLines[dateToKey(date)]?.map(line => {
                                    const title = eventTypes?.find(x => x.entityShortName === line.event.type)?.entityShortNameLocalised
                                    return <div
                                        title={title}
                                        css={css`
                                            position: absolute;
                                            bottom: 0;
                                            left: ${line.startPercentage}%;
                                            right: ${100 - line.endPercentage}%;
                                            height: 6px;
                                            opacity: 0.33;
                                            box-sizing: unset;
                                        `} >
                                        <div title={title} css={css` height: 2px; top: 4px; background: ${colors.main1}; position: relative; `} />
                                    </div>
                                })}
                            </DayDropZone>
                        )}>
                        {Object.entries(eventsPerDay)
                            .map(
                                ([key, events]) => ({
                                    key,
                                    node: Object.entries(countBy(events, 'type'))
                                        .map(([eventType, count]) => {
                                            if ((filter?.length === 0) || filter.includes(eventType)) {
                                                return <EventTypeIcon
                                                    eventType={eventType}
                                                    counter={(count >= 2) ? count : null}
                                                    title={eventTypes?.find(x => x.entityShortName === eventType)?.entityShortNameLocalised}
                                                    css={css`cursor: pointer;`}
                                                    key={`${eventType}-${key}`}
                                                />
                                            } else {
                                                return <span css={styles.hiddenDataNotifier} title={formatMessage({ id: 'eventsOmmittedByFilter' })}  >
                                                    <ReactSVG
                                                        src={filterImg}
                                                        onClick={handleClearFilter}
                                                    />
                                                </span>
                                            }
                                        })
                                })
                            )}
                    </MonthlyCalendar>
                    <EventTypeList />
                </div>
            </div >
            {daySummaryData?.[0] &&
                <DaySummaryModal
                    daySummaryData={daySummaryData}
                    setDaySummaryData={setDaySummaryData}
                    deleteEvent={deleteEvent}
                    setSelectedEvent={setSelectedEvent}
                />
            }

            {(selectedEventType || selectedEvent) && !loadingEventTypes &&
                <Modal
                    header={getFormHeader()}
                    onClickClose={() => {
                        setSelectedEventType(null)
                        setSelectedEvent(null)
                        setFakeEvents(current => current?.slice(0, -1) ?? [])
                    }}
                    css={css`width: auto; overflow: visible;`}>
                    <EventWrapper
                        eventType={
                            selectedEventType
                                ? selectedEventType
                                : selectedEvent.type
                        }
                        event={selectedEvent}
                        pitch={pitch}
                        onPostRequest={(success, data) => handleModalSubmit(success, data)}
                    />
                </Modal>
            }
        </>
    );
}

export default PitchEventCalendarDev;

const styles = {
    pitchSelector: css`
        width: 17.5em;
        z-index: 2000;
    `,
    calendarAndEventsWrapper: css`
        height: 70vh;
        margin-top: 1em;
        userSelect: none;
        position: relative;
    `,
    calendar: css`
        flex-grow: 1;
        margin-right: 1em;
        height: 100%;
    `,
    hiddenDataNotifier: css`
        fill: ${colors.liquid};
        position: absolute;
        top: 0.2em;
        right: 0.2em;
        width: 1em;
        height: 1em;
        border: solid 1px ${colors.liquid};
        cursor: pointer;

        &:hover {
            fill: ${colors.soft};
        }
    `
}
